import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Footprints, Compass, Users, Sparkles, ArrowRight, Zap, Telescope } from "lucide-react";
import api from "@/lib/api";
import EventCard from "@/components/EventCard";
import RegisterModal from "@/components/RegisterModal";
import PageHero from "@/components/PageHero";

const RUNNERS_GET = ["New running experiences", "Scenic and challenging routes", "Destination discovery", "Travel with purpose", "Community interaction"];
const DESTINATIONS_GET = ["National visibility", "Increased visitor footfall", "Tourism promotion", "Local economic activity", "Community participation", "Fitness awareness"];
const MOTIVATIONS = ["Fitness", "Adventure", "Participation", "Social Interaction", "Travel", "Destination Discovery", "Culture & Heritage", "A Break from Routine"];

export default function Runsafari() {
  const [projects, setProjects] = useState([]);
  const [regEvent, setRegEvent] = useState(null);

  useEffect(() => {
    api.get("/events?category=RUNSAFARI").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="runsafari-page">
      <PageHero eyebrow="SPORTIVE'S FLAGSHIP CONCEPT" title="RUNSAFARI — Run Beyond The Route."
        subtitle="RUNSAFARI is SPORTIVE's destination-running concept that combines running events with travel, adventure, culture and local experiences. Instead of simply travelling to a destination to participate in a race, RUNSAFARI encourages participants to experience the destination through the run."
        image="https://images.unsplash.com/photo-1697222564092-f4dab603efa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxtYXJhdGhvbiUyMHRyYWlsJTIwcnVubmluZyUyMHN1bnNldCUyMG1vdW50YWlucyUyMGF0aGxldGV8ZW58MHx8fHwxNzg5MDY4MTE1fDA&ixlib=rb-4.1.0&q=85" />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <p className="eyebrow">THE RUNSAFARI EXPERIENCE</p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[[Footprints, "RUN", "Challenge yourself across unique landscapes and routes.", "text-brand-cyan"],
            [Compass, "EXPLORE", "Discover places beyond conventional tourist circuits.", "text-brand-olive"],
            [Users, "CONNECT", "Meet local communities, runners, travellers and fellow participants.", "text-brand-cyan"],
            [Sparkles, "EXPERIENCE", "Discover regional culture, food, heritage, nature and adventure.", "text-brand-olive"]].map(([Icon, t, d, c]) => (
            <div key={t} className="card-k p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-cyan/40">
              <Icon size={24} className={c} />
              <h3 className="mt-4 font-display text-2xl font-bold text-white">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-brand-line bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="eyebrow">WHY RUNSAFARI?</p>
          <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold uppercase tracking-tight text-white">Where Running Meets Tourism</h2>
          <p className="mt-4 max-w-2xl text-slate-400">Running has the power to take people places. RUNSAFARI uses this connection to create destination experiences that bring together <span className="text-brand-cyan">Fitness + Adventure + Tourism + Culture + Community</span>.</p>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="card-k border-brand-cyan/30 p-8">
              <p className="font-mono text-xs tracking-[0.25em] text-brand-cyan">FOR RUNNERS</p>
              <ul className="mt-5 space-y-3">
                {RUNNERS_GET.map((r) => <li key={r} className="flex items-center gap-3 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" />{r}</li>)}
              </ul>
            </div>
            <div className="card-k border-brand-olive/30 p-8">
              <p className="font-mono text-xs tracking-[0.25em] text-brand-olive">FOR DESTINATIONS</p>
              <ul className="mt-5 space-y-3">
                {DESTINATIONS_GET.map((r) => <li key={r} className="flex items-center gap-3 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full bg-brand-olive" />{r}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <p className="eyebrow">THE RUNSAFARI PARTICIPANT</p>
        <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold uppercase tracking-tight text-white">Every Runner Has a Different Story.</h2>
        <p className="mt-4 max-w-2xl text-slate-400">RUNSAFARI welcomes everyone — from experienced amateur runners looking for their next challenge to first-time runners seeking an unforgettable experience.</p>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="card-k p-8">
            <Zap className="text-brand-cyan" size={26} />
            <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">The Challenge Seeker</h3>
            <p className="mt-1 font-mono text-xs tracking-[0.15em] text-brand-cyan">ENERGETIC. ADVENTUROUS. TRAVEL-DRIVEN.</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">Looking for challenging routes, unique destinations and memorable experiences.</p>
          </div>
          <div className="card-k p-8">
            <Telescope className="text-brand-olive" size={26} />
            <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">The Experience Seeker</h3>
            <p className="mt-1 font-mono text-xs tracking-[0.15em] text-brand-olive">CURIOUS. MOTIVATED. DISCOVERY-ORIENTED.</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">Looking for a first destination-running experience combined with travel and exploration.</p>
          </div>
        </div>
        <div className="mt-12">
          <p className="font-mono text-xs tracking-[0.25em] text-slate-500">WHAT BRINGS THEM TO THE START LINE?</p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {MOTIVATIONS.map((m) => <span key={m} className="rounded-full border border-brand-line bg-brand-surface px-4 py-2 text-xs text-slate-300 transition-colors hover:border-brand-cyan hover:text-brand-cyan">{m}</span>)}
          </div>
        </div>
      </section>

      {projects.length > 0 && (
        <section className="border-t border-brand-line bg-brand-surface/40">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow">SELECTED RUNSAFARI PROJECTS</p>
                <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">The Journey So Far</h2>
              </div>
              <Link to="/events" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-brand-cyan hover:gap-3 transition-all">ALL EVENTS <ArrowRight size={14} /></Link>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((e) => <EventCard key={e.slug} event={e} onRegister={setRegEvent} />)}
            </div>
          </div>
        </section>
      )}

      {regEvent && <RegisterModal event={regEvent} onClose={() => setRegEvent(null)} />}
    </div>
  );
}
